import * as React from 'react';
import _ from 'lodash';
import { Field, Form, FormRenderProps } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import FontAwesomeIcon from 'react-fontawesome';
import classnames from 'classnames';
import { FormApi, ValidationErrors } from 'final-form';
import { OnChange } from 'react-final-form-listeners';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import { Store } from './types';
import { enqueueNotification, generatePDF, saveUpdateTable } from './actions/Actions';
import { required, TABLES_NS } from './Constants';

interface TableFormValues {
    id: number;
    description: string;
    code: string;
    allowedPeople: number;
}

enum PdfCreationType {
    NOT_AVAILABLE,
    A5,
    A6,
    A7Light
}

interface PrintFormValues {
    type: string;
    printLanguageIDs: string[];
    tableIDToPrint: TableToPrint;
}

enum TableToPrint {
    CURRENT = 'CURRENT',
    ALL = 'ALL'
}

const Tables: React.FC<RouteComponentProps> = () => {
    const dispatch: ThunkDispatch<Store, unknown, AnyAction> = useDispatch();
    const storeConfiguration = useSelector(({ app }: Store) => app.storeConfiguration);
    const availableLanguages = useSelector(({ app }: Store) => app.availableLanguages);
    const tables = useSelector(({ app }: Store) => app.tables);
    const { t } = useTranslation();

    const getInitialPrintFormValues = React.useCallback(
        () => ({
            type: PdfCreationType.A5.toString(),
            tableIDToPrint: TableToPrint.ALL,
            printLanguageIDs: [availableLanguages[0].id.toString()]
        }),
        [availableLanguages]
    );

    const [tableLoading, setTableLoading] = React.useState(false);
    const [printLoading, setPrintLoading] = React.useState(false);
    const [selectedTableId, setSelectedTableId] = React.useState<number>(null);
    const [tableFormValues, setTableFormValues] = React.useState<
        Partial<TableFormValues>
    >({
        allowedPeople: storeConfiguration.defaultAllowedPeoplePerTable
    });
    const [printFormValues, setPrintFormValues] = React.useState<
        Partial<PrintFormValues>
    >(getInitialPrintFormValues());

    const validatePrintOptions = React.useCallback(
        (values: PrintFormValues) => {
            const errors: ValidationErrors = {};

            if (Number(values.type) === PdfCreationType.A7Light) {
                if (values.printLanguageIDs.length > 2) {
                    errors.printLanguageIDs = t(`${TABLES_NS}.max_lang_selection`);
                }
            } else if (values.printLanguageIDs.length > 1) {
                errors.printLanguageIDs = t(`${TABLES_NS}.lang_selection_validation`);
            }

            return errors;
        },
        [t]
    );

    const validateTableFormValues = React.useCallback(
        (values: TableFormValues) => {
            const errors: ValidationErrors = {};

            if (values.code && values.code.length > 5) {
                errors.code = t(`${TABLES_NS}.table_code_validation`);
            }

            return errors;
        },
        [t]
    );

    const setTableId = React.useCallback(
        (tableId: number) => () => {
            setSelectedTableId(tableId);
            setTableFormValues(tables.find(({ id }) => id === tableId));
            setPrintFormValues({
                ...printFormValues,
                tableIDToPrint: TableToPrint.CURRENT
            });
        },
        [printFormValues, tables]
    );

    const resetStateAndForm = React.useCallback(() => {
        setSelectedTableId(null);
        setTableFormValues({
            allowedPeople: storeConfiguration.defaultAllowedPeoplePerTable
        });
        setPrintFormValues(getInitialPrintFormValues());
    }, [getInitialPrintFormValues, storeConfiguration.defaultAllowedPeoplePerTable]);

    const renderPrintOptions = React.useCallback(
        ({
            handleSubmit,
            submitting,
            validating,
            values,
            errors
        }: FormRenderProps<PrintFormValues>) => {
            const disabled = submitting || validating || printLoading;

            return (
                <form onSubmit={handleSubmit} className='form print-options'>
                    <div className='form-title'>{t(`${TABLES_NS}.print_title`)}</div>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${TABLES_NS}.print_dimensions`)}:
                        </label>
                        <div className='container'>
                            <label className='radio-button-container'>
                                Α5 (148.5x210mm)
                                <Field
                                    name='type'
                                    component='input'
                                    type='radio'
                                    value={PdfCreationType.A5.toString()}
                                />
                                <span className='checkmark' />
                            </label>
                            <label className='radio-button-container'>
                                Α6 (105x148.5mm)
                                <Field
                                    name='type'
                                    component='input'
                                    type='radio'
                                    value={PdfCreationType.A6.toString()}
                                />
                                <span className='checkmark' />
                            </label>
                            <label className='radio-button-container'>
                                Α7 LIGHT (74.25x105mm)
                                <Field
                                    name='type'
                                    component='input'
                                    type='radio'
                                    value={PdfCreationType.A7Light.toString()}
                                />
                                <span className='checkmark' />
                            </label>
                            <OnChange name='type'>
                                {value => {
                                    setPrintFormValues({
                                        ...values,
                                        type: value
                                    });
                                }}
                            </OnChange>
                        </div>
                    </label>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${TABLES_NS}.print_languages`)}
                            {Number(printFormValues.type) === PdfCreationType.A7Light
                                ? ' (2)'
                                : ''}
                            :
                        </label>
                        <div className='container'>
                            {availableLanguages.map(({ id, name }) => (
                                <label className='checkbox-container' key={id}>
                                    {name.toUpperCase()}
                                    <Field
                                        name='printLanguageIDs'
                                        component='input'
                                        type='checkbox'
                                        value={id.toString()}
                                    />
                                    <span className='checkmark' />
                                </label>
                            ))}
                        </div>
                        {errors.printLanguageIDs && (
                            <FontAwesomeIcon
                                className='error-icon'
                                name='exclamation-circle'
                                title={errors.printLanguageIDs}
                            />
                        )}
                    </label>
                    <label className='form-field'>
                        <label className='wide'>{t(`${TABLES_NS}.pdf_file`)}:</label>
                        <div className='container'>
                            {!_.isNil(tableFormValues.id) && (
                                <label className='radio-button-container'>
                                    {t(`${TABLES_NS}.pdf_current_table`)}
                                    <Field
                                        name='tableIDToPrint'
                                        component='input'
                                        type='radio'
                                        value={TableToPrint.CURRENT}
                                    />
                                    <span className='checkmark' />
                                </label>
                            )}
                            <label className='radio-button-container'>
                                {t(`${TABLES_NS}.pdf_all_tables`)}
                                <Field
                                    name='tableIDToPrint'
                                    component='input'
                                    type='radio'
                                    value={TableToPrint.ALL}
                                />
                                <span className='checkmark' />
                            </label>
                        </div>
                        <OnChange name='tableIDToPrint'>
                            {value => {
                                setPrintFormValues({
                                    ...values,
                                    tableIDToPrint: value
                                });
                            }}
                        </OnChange>
                    </label>
                    <div className='action-buttons'>
                        <div>
                            <button className='save' type='submit' disabled={disabled}>
                                {t(`${TABLES_NS}.pdf_create`)}
                            </button>
                            {printLoading && (
                                <FontAwesomeIcon name='spinner' className='fa-spin' />
                            )}
                        </div>
                    </div>
                    <div className='download-info'>
                        {t(`${TABLES_NS}.pdf_create_info`)}
                    </div>
                </form>
            );
        },
        [availableLanguages, printFormValues.type, printLoading, t, tableFormValues.id]
    );

    const renderTableForm = React.useCallback(
        ({ handleSubmit, submitting, validating }: FormRenderProps) => {
            const disabled = submitting || validating;

            return (
                <form onSubmit={handleSubmit} className='form'>
                    <label className='form-field'>
                        <label className='small-wide'>
                            {t(`${TABLES_NS}.table_code`)}:
                        </label>
                        <Field name='code' validate={required(t('required_field'))}>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input {...input} type='text' />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='small-wide'>
                            {t(`${TABLES_NS}.table_description`)}:
                        </label>
                        <Field
                            name='description'
                            validate={required(t('required_field'))}>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input {...input} type='text' />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='small-wide'>
                            {t(`${TABLES_NS}.table_seats`)}:
                        </label>
                        <Field
                            name='allowedPeople'
                            validate={required(t('required_field'))}>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input {...input} type='number' min={0} />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <div className='action-buttons'>
                        <div>
                            <button className='save' type='submit' disabled={disabled}>
                                {t('save')}
                            </button>
                            {tableLoading && (
                                <FontAwesomeIcon name='spinner' className='fa-spin' />
                            )}
                        </div>
                        {/* <button className='delete' type='button' disabled={disabled}>
                        ΔΙΑΓΡΑΦΗ
                    </button> */}
                    </div>
                </form>
            );
        },
        [t, tableLoading]
    );

    const handleTableSubmit = React.useCallback(
        (values: TableFormValues, form: FormApi) => {
            setTableLoading(true);

            dispatch(saveUpdateTable(values))
                .then(() => {
                    resetStateAndForm();
                    form.reset({});

                    ['code', 'description', 'allowedPeople'].forEach(key => {
                        form.resetFieldState(key);
                    });

                    const message = _.isNil(values.id)
                        ? t(`${TABLES_NS}.table_added`, values)
                        : t(`${TABLES_NS}.table_updated`, values);

                    dispatch(
                        enqueueNotification({
                            key: new Date().getTime(),
                            message,
                            options: {
                                variant: 'success'
                            }
                        })
                    );
                })
                .finally(() => {
                    setTableLoading(false);
                });
        },
        [dispatch, resetStateAndForm, t]
    );

    const handlePrint = React.useCallback(
        (values: PrintFormValues, form: FormApi<PrintFormValues>) => {
            setPrintLoading(true);

            const printOptions = {
                type: Number(values.type),
                printLanguageIDs: values.printLanguageIDs.map(Number),
                tableIDToPrint:
                    values.tableIDToPrint === TableToPrint.CURRENT
                        ? selectedTableId
                        : null
            };

            dispatch(generatePDF(printOptions))
                .then(() => {
                    setPrintFormValues(getInitialPrintFormValues());
                    form.reset(getInitialPrintFormValues());

                    ['type', 'printLanguageIDs', 'tableIDToPrint'].forEach(
                        (key: keyof PrintFormValues) => {
                            form.resetFieldState(key);
                        }
                    );
                })
                .catch(e => {
                    dispatch(
                        enqueueNotification({
                            key: new Date().getTime(),
                            message: e.message,
                            options: {
                                variant: 'error'
                            }
                        })
                    );
                })
                .finally(() => {
                    setPrintLoading(false);
                });
        },
        [dispatch, getInitialPrintFormValues, selectedTableId]
    );

    return (
        <div className='admin-form main-content'>
            <Sidebar>
                <FontAwesomeIcon
                    name='plus'
                    className='add-new'
                    onClick={resetStateAndForm}
                />
                <div className='content'>
                    {tables.map(({ id, code }) => (
                        <div
                            className={classnames('item', {
                                selected: id === selectedTableId
                            })}
                            key={id}
                            onClick={setTableId(id)}>
                            {code}
                        </div>
                    ))}
                </div>
            </Sidebar>
            <div className='tables-form'>
                <Form
                    render={renderTableForm}
                    onSubmit={handleTableSubmit}
                    validate={validateTableFormValues}
                    initialValues={{ ...tableFormValues }}
                />
                <Form
                    render={renderPrintOptions}
                    onSubmit={handlePrint}
                    validate={validatePrintOptions}
                    initialValues={{ ...printFormValues }}
                />
            </div>
        </div>
    );
};

export default Tables;
